import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MapPin, Phone, Mail, Clock, MessageCircle, Instagram, Facebook, Loader2, Check } from 'lucide-react';
import { submitContact } from '@/lib/api';
import { toast } from 'sonner';

export default function Contatti() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await submitContact(formData);
      setSubmitted(true);
      toast.success('Messaggio inviato con successo!');
    } catch (error) {
      toast.error('Errore durante l\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-ivory pt-32 min-h-screen" data-testid="contatti-page">
      {/* Header */}
      <section className="pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          <motion.p
            className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Contatti
          </motion.p>
          <motion.h1
            className="font-heading text-5xl md:text-6xl text-night tracking-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="page-title"
          >
            Parliamo
          </motion.h1>
          <motion.p
            className="font-body text-lg text-stone-500 max-w-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Hai domande o vuoi saperne di più? Siamo qui per aiutarti.
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="space-y-8 mb-12">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-champagne/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-champagne" size={20} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-night mb-1">Indirizzo</h3>
                    <p className="font-body text-stone-600">
                      Via Emilia Est 85/b<br />
                      41013 Castelfranco Emilia (MO)<br />
                      Italia
                    </p>
                    <p className="font-body text-xs text-champagne mt-2">
                      20 min da Modena • 20 min da Bologna
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-champagne/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-champagne" size={20} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-night mb-1">Telefono</h3>
                    <a
                      href="tel:+393514504629"
                      className="font-body text-stone-600 hover:text-champagne transition-colors"
                    >
                      +39 351 450 4629
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-champagne/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-champagne" size={20} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-night mb-1">Email</h3>
                    <a
                      href="mailto:lavilla.cf@gmail.com"
                      className="font-body text-stone-600 hover:text-champagne transition-colors"
                    >
                      lavilla.cf@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-champagne/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="text-champagne" size={20} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-night mb-1">Orari</h3>
                    <p className="font-body text-stone-600">
                      Piscina: 09:00 - 19:30<br />
                      Eventi serali: 20:00 - 02:00<br />
                      Eventi speciali: 22:00 - 04:00
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="space-y-4">
                <a
                  href="https://wa.me/393514504629?text=Ciao!%20Vorrei%20informazioni%20su%20La%20Villa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-green-500 text-white hover:bg-green-600 transition-colors"
                  data-testid="whatsapp-link"
                >
                  <MessageCircle size={24} />
                  <div>
                    <p className="font-body font-semibold">WhatsApp</p>
                    <p className="font-body text-sm opacity-90">Rispondiamo subito</p>
                  </div>
                </a>

                <div className="flex gap-4">
                  <a
                    href="https://www.instagram.com/la_villa.cf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 p-4 bg-stone-100 text-night hover:bg-stone-200 transition-colors"
                    data-testid="instagram-link"
                  >
                    <Instagram size={20} />
                    <span className="font-body text-sm">@la_villa.cf</span>
                  </a>
                  <a
                    href="https://www.facebook.com/LaVillaCF"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 p-4 bg-stone-100 text-night hover:bg-stone-200 transition-colors"
                    data-testid="facebook-link"
                  >
                    <Facebook size={20} />
                    <span className="font-body text-sm">Facebook</span>
                  </a>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="mt-12" id="mappa">
                <div className="aspect-video overflow-hidden rounded-lg shadow-lg">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2838.8!2d11.0547!3d44.5947!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477e2b0f5f5f5f5f%3A0x0!2sVia%20Emilia%20Est%2085%2Fb%2C%2041013%20Castelfranco%20Emilia%20MO!5e0!3m2!1sit!2sit!4v1709500000000!5m2!1sit!2sit"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="La Villa - Come Raggiungerci"
                    className="w-full h-full"
                  />
                </div>
                <p className="font-body text-xs text-stone-400 mt-3 text-center">
                  Via Emilia Est 85/b, 41013 Castelfranco Emilia (MO)
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="bg-white p-8 md:p-12 shadow-soft">
                {submitted ? (
                  <div className="text-center py-12">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-16 h-16 bg-champagne rounded-full flex items-center justify-center mx-auto mb-6"
                    >
                      <Check className="text-white" size={32} />
                    </motion.div>
                    <h3 className="font-heading text-2xl text-night mb-2">Messaggio Inviato!</h3>
                    <p className="font-body text-stone-500">
                      Ti risponderemo il prima possibile.
                    </p>
                  </div>
                ) : (
                  <>
                    <h2 className="font-heading text-2xl text-night mb-6">Scrivici</h2>
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div>
                        <Label className="font-body text-sm text-stone-600">Nome e Cognome *</Label>
                        <Input
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-2"
                          placeholder="Mario Rossi"
                          data-testid="contact-name"
                        />
                      </div>

                      <div>
                        <Label className="font-body text-sm text-stone-600">Email *</Label>
                        <Input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="mt-2"
                          placeholder="mario@esempio.com"
                          data-testid="contact-email"
                        />
                      </div>

                      <div>
                        <Label className="font-body text-sm text-stone-600">Telefono</Label>
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="mt-2"
                          placeholder="+39 333 123 4567"
                          data-testid="contact-phone"
                        />
                      </div>

                      <div>
                        <Label className="font-body text-sm text-stone-600">Messaggio *</Label>
                        <Textarea
                          required
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          className="mt-2"
                          rows={5}
                          placeholder="Come possiamo aiutarti?"
                          data-testid="contact-message"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-night text-ivory hover:bg-stone-800 rounded-full py-6 text-sm uppercase tracking-widest"
                        data-testid="contact-submit"
                      >
                        {loading ? (
                          <Loader2 className="animate-spin" size={20} />
                        ) : (
                          'Invia Messaggio'
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom padding for mobile CTA */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
